import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PublicQuoteSelection, PublicQuoteSubmission, PublicQuoteView } from '@bandido/shared/types';
import { submitPublicQuote } from './publicQuoteTransport';
export function quoteContactUrl(phone: string | null | undefined, message: string): string | undefined {
  return phone && /^[1-9][0-9]{7,14}$/.test(phone)
    ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    : undefined;
}
export function PublicQuoteActions({
  token,
  view,
  selection,
  onBusy,
  onLatest,
  disabled = false,
  compact = false,
}: {
  disabled?: boolean;
  compact?: boolean;
  token: string;
  view: PublicQuoteView;
  selection: PublicQuoteSelection;
  onBusy: (busy: boolean) => void;
  onLatest: () => void;
}) {
  const { t } = useTranslation();
  const tr = (key: string, values?: Record<string, string | number>) => t(`commercialQuotes.selection.${key}`, values);
  const [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false),
    [sent, setSent] = useState<PublicQuoteSubmission>();
  const active = useRef(true),
    pending = useRef(false),
    retry = useRef<{ intent: string; id: string }>();
  const intent = JSON.stringify([view.versionId, Object.entries(selection).sort(([a], [b]) => a.localeCompare(b))]);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    setSent(undefined);
    setFailed(false);
  }, [intent]);
  const inquiry = quoteContactUrl(
    view.contact?.phone,
    tr('inquiryMessage', {
      title: view.snapshot.title,
      version: view.version,
      reference: view.contact?.inquiryReference ?? '',
    }),
  );
  const continuation = (result: PublicQuoteSubmission) =>
    quoteContactUrl(
      result.contact.phone,
      tr('selectionMessage', {
        title: view.snapshot.title,
        version: result.version,
        reference: result.reference,
      }),
    );
  async function submit() {
    if (pending.current || view.archived || disabled) return;
    pending.current = true;
    setBusy(true);
    onBusy(true);
    setFailed(false);
    // Open during the user gesture; navigation happens only after the save succeeds.
    // Detach the opener before navigating to the external messaging site.
    let popup: Window | null = null;
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 15000);
    try {
      if (retry.current?.intent !== intent) retry.current = { intent, id: crypto.randomUUID() };
      try {
        popup = inquiry ? window.open('about:blank', '_blank') : null;
        if (popup) popup.opener = null;
      } catch {
        popup = null;
      }
      const result = await submitPublicQuote(token, view.versionId, selection, retry.current.id, abort.signal);
      if (!active.current) {
        popup?.close();
        return;
      }
      setSent(result);
      const url = continuation(result);
      if (url && popup && !popup.closed) popup.location.href = url;
      else popup?.close();
    } catch {
      popup?.close();
      if (active.current) setFailed(true);
    } finally {
      clearTimeout(timer);
      pending.current = false;
      if (active.current) {
        setBusy(false);
        onBusy(false);
      }
    }
  }
  return (
    <Stack spacing={1}>
      {failed && <Alert severity="warning">{tr('failed')}</Alert>}
      {sent && (
        <Alert severity="success">
          <Typography variant="body2">{tr('saved')}</Typography>
        </Alert>
      )}
      {sent?.updated && (
        <Alert severity="info" action={<Button onClick={onLatest}>{tr('latest')}</Button>}>
          {tr('oldVersion')}
        </Alert>
      )}
      {!view.archived &&
        (!sent ? (
          <Button variant="contained" disabled={busy || disabled} onClick={() => void submit()}>
            {tr(busy ? 'saving' : inquiry ? 'advance' : 'save')}
          </Button>
        ) : (
          continuation(sent) && (
            <Button variant="contained" component="a" href={continuation(sent)} target="_blank" rel="noreferrer">
              {tr('open')}
            </Button>
          )
        ))}
      {!compact && (inquiry ? (
        <Button component="a" href={inquiry} target="_blank" rel="noreferrer">
          {tr('consult')}
        </Button>
      ) : (
        view.snapshot.brand.phone && (
          <Button component="a" href={`tel:${view.snapshot.brand.phone.replace(/[^+\d]/g, '')}`}>
            {tr('call')}
          </Button>
        )
      ))}
      {sent && <Typography variant="caption">{tr(continuation(sent) ? 'sendHint' : 'contactHint')}</Typography>}
    </Stack>
  );
}
